import type { AnswerValue, Attempt, Question } from '../types'

export const normalize = (value: string) => value.trim().replace(/\s+/g, '').replace(',', '.')

export function isCorrect(question: Question, value: AnswerValue): boolean {
  if (question.type === 'numeric') return normalize(String(value)) === normalize(String(question.answer))
  if (question.type === 'single-choice') return value === question.answer
  if (Array.isArray(value) && Array.isArray(question.answer)) {
    return JSON.stringify([...value].sort()) === JSON.stringify([...question.answer].sort())
  }
  return false
}

export function competencyScores(attempts: Attempt[]) {
  const bucket = new Map<string, { right: number; total: number }>()
  attempts.forEach(a => a.competency.forEach(c => {
    const x = bucket.get(c) ?? { right: 0, total: 0 }
    x.total += 1
    x.right += a.correct ? 1 : 0
    bucket.set(c, x)
  }))
  return [...bucket.entries()]
    .map(([name, x]) => ({ name, score: Math.round((x.right / x.total) * 100), total: x.total }))
    .sort((a, b) => a.score - b.score)
}

export function pickAdaptive(questions: Question[], attempts: Attempt[], count = 10) {
  const done = new Set(attempts.map(a => a.questionId))
  const scores = competencyScores(attempts)
  const weakest = scores.slice(0, 3).map(s => s.name)
  const hasFreshSessionVariant = questions.some(q => !done.has(q.id) && q.tags.includes('session-variant'))
  return questions
    .filter(q => !done.has(q.id))
    .sort((a, b) => {
      // Parameterized items are newly created for each practice session. Prefer
      // them when available so a learner never runs out of valid unseen items.
      if (hasFreshSessionVariant) {
        const av = a.tags.includes('session-variant') ? 1 : 0
        const bv = b.tags.includes('session-variant') ? 1 : 0
        if (av !== bv) return bv - av
      }
      const aw = a.competency.some(c => weakest.includes(c)) ? 1 : 0
      const bw = b.competency.some(c => weakest.includes(c)) ? 1 : 0
      if (aw !== bw) return bw - aw
      
      // If competencies are equally relevant, sort randomly so it's different each time
      return Math.random() - 0.5
    })
    .slice(0, count)
}

export const overallScore = (attempts: Attempt[]) => attempts.length ? Math.round(attempts.filter(a => a.correct).length / attempts.length * 100) : 0
